<template>
  <div data-app>
    <div class="filter-box md-layout-item md-size-100 md-small-size-100 mt-5">
      <div
        class="application-popup-overlay"
        v-if="showApplications"
        v-on:click="showApplications = false"
      />

      <div class="application-popup" v-if="showApplications">
        <application-list
          v-if="totalApplications && totalApplications.length > 0"
          :items="totalApplications"
          :backend-pagination="true"
          :external-data-refresh="true"
          :external-loading-pagination="paginationLoading"
          @update="openApplicationEditor"
          @view="openApplicationSummary"
          @update-status="openUpdateStatusDialog"
          @manage-owners="openManageOwnersDialog"
          @attachments="openApplicationDocuments"
          @comment="openCommentDialog"
          @clone="onCloneApplication"
          @refresh-data="refreshData"
        />
        <md-subheader v-else>No applications found</md-subheader>
      </div>
    </div>
    <div class="md-layout input-layout">
      <!-- From date field-->
      <div class="md-layout-item md-small-size-100 md-size-10">
        <md-datepicker
          md-immediately
          class="reporting-date-picker"
          v-model="filters.date_filter.start_date"
        >
          <label>From date</label>
        </md-datepicker>
      </div>

      <!-- To date field-->
      <div class="md-layout-item md-small-size-100 md-size-10">
        <md-datepicker
          md-immediately
          v-model="filters.date_filter.end_date"
        >
          <label>To date</label>
        </md-datepicker>
      </div>
      <!-- asset type field-->
      <validated-autocomplete-input
        class="md-layout-item md-small-size-100 md-size-10"
        label="Asset Types"
        v-model="filters.dropdown.asset_type"
        :options="options.loan_asset_type"
        v-if="options.loan_asset_type"
        force
        default-option
        :focus="false"
        multiple
      >
      </validated-autocomplete-input>
      <!-- pods field-->
      <validated-autocomplete-input
        class="md-layout-item md-small-size-100 md-size-15"
        label="Pods" v-model="filters.dropdown.pod"
        :options="options.broker_pods"
        v-if="options.broker_pods"
        force
        :focus="false"
        multiple
      >
      </validated-autocomplete-input>
      <!-- broker field -->
      <validated-autocomplete-input
        v-if="filters.dropdown.pod"
        class="md-layout-item md-small-size-100 md-size-10"
        label="Broker" v-model="filters.dropdown.broker"
        :options="podBrokerList"
        force
        default-option
        multiple
        :focus="false"
      >
      </validated-autocomplete-input>
      <!--role type field-->
      <validated-autocomplete-input
        class="md-layout-item md-small-size-100 md-size-10"
        label="Roles"
        v-model="filters.dropdown.roles"
        :options="options.roles"
        v-if="options.roles"
        force
        default-option
        :focus="false"
        multiple
      >
      </validated-autocomplete-input>
      <!-- lender field-->
      <validated-autocomplete-input
        class="md-layout-item md-small-size-100 md-size-10"
        label="Lender" v-model="filters.dropdown.lender"
        :options="options.lenders"
        v-if="options.lenders"
        force
        default-option
        :focus="false"
        multiple
      >
      </validated-autocomplete-input>
      <!-- introducer field-->
      <span class="md-layout-item md-small-size-100 md-size-10">
        <md-autocomplete
          :md-options="introducerList.map(x=>({
            'id':x.id,
            'name':x.name,
            'toLowerCase':()=>x.name.toLowerCase(),
            'toString':()=>x.name
          }))"
          v-model="filters.dropdown.introducer"
        >
          <label>Referrer</label>
          <template slot="md-autocomplete-item" slot-scope="{ item }">{{ item }}</template>
        </md-autocomplete>
      </span>
    </div>
    <br>

    <md-card-actions v-if="showVBI">
      <loading-icon-text-button
        class="md-primary md-sm"
        icon="assignment"
        :loading="false"
        v-if="(this.applicationIds.length > 0) && this.allowSeeApplications"
        @click="getApplicationList(applicationIds)"
      >
        See Applications
      </loading-icon-text-button>
      <loading-icon-text-button
        class="md-primary md-sm"
        icon="file_download"
        :loading="false"
        @click="exportApplications"
        v-if="this.allowExport"
      >
        Export
      </loading-icon-text-button>
      <loading-icon-text-button
        class="md-primary md-sm"
        icon="restart_alt"
        :loading="false"
        @click="resetFilters"
      >
        Reset
      </loading-icon-text-button>
      <loading-icon-text-button
        class="md-primary md-sm"
        icon="search"
        :loading="false"
        @click="applyFilter"
      >
        Apply
      </loading-icon-text-button>
    </md-card-actions>

    <md-tabs class="md-secondary" md-alignment="centered" @md-changed="tabChanged">
      <md-tab id="tab-vbi" md-label="VBI" :disabled="!showVBI">
        <vbi-reporting
          v-if="showVBI"
          :vbiTabGraph="vbiTabGraph"
          :roundedNaf="roundedNaf"
          :roundedPodNaf="roundedPodNaf"
          :filters="filters"/>
      </md-tab>
    </md-tabs>

    <group-card v-if="this.firstLoad && this.showVBI">
      <md-empty-state
        md-description="Apply filter to fetch the reporting data."
      />
    </group-card>
  </div>
</template>

<script>
import VbiReporting from "@/pages/VbiReporting";
import StatCardWithIcon from "./ReportingDashboard/components/StatCardWithIcon";
import BarChart from "./ReportingDashboard/charts/BarChart";
import ValidatedInput from "../components/ValidatedInput/ValidatedInput";
import ValidatedSelectInput from "../components/ValidatedInput/ValidatedSelectInput";
import GroupCard from "../components/Cards/GroupCard";
import LoadingIconTextButton from "../components/LoadingIconTextButton";
import {mapGetters} from "vuex";
import ValidatedAutocompleteInput from "../components/ValidatedInput/ValidatedAutocompleteInput";
import WaitingDialog from "../components/Dialogs/WaitingDialog";
import {newReportingFilters} from "../schema";
import PieChart from "./ReportingDashboard/charts/PieChart";
import DoughChart from "./ReportingDashboard/charts/DoughChart";
import NotificationCard from "../components/Cards/NotificationCard";
import LineChart from "./ReportingDashboard/charts/LineChart";
import AlertDialog from "../components/Dialogs/AlertDialog";
import ApplicationList from "@/components/ApplicationList";
import ConfirmDialog from "@/components/Dialogs/ConfirmDialog";
import moment from "moment";

export default {
  components: {
    VbiReporting,
    NotificationCard,
    DoughChart,
    ValidatedInput,
    StatCardWithIcon,
    BarChart,
    GroupCard,
    ValidatedSelectInput,
    LoadingIconTextButton,
    ValidatedAutocompleteInput,
    PieChart,
    LineChart,
    ApplicationList,
    ConfirmDialog,
    WaitingDialog,
    AlertDialog,
    GroupCard
  },
  data() {
    return {
      brokerList: [],
      podBrokerList: [],
      introducerList: [],
      graphOption: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            color: 'black',
            backgroundColor: 'white',
            textAlign: 'center',
            borderRadius: 5,
          }
        },
      },
      filters: new newReportingFilters(),
      showApplications: false,
      vbiTabGraph: [],
      currentTabId: "tab-vbi",
      allowExport: false,
      allowSeeApplications: false,
      updateRequireForVBITab: true,
      roundedNaf: [],
      roundedPodNaf: [],
      roundedTotalIncome: [],
      totalApplications: [],
      showApplications: false,
      applicationIds: [],
      showVBI: false,
      firstLoad: true,
      paginationLoading: false
    };
  },
  methods: {
    tabChanged(id) {
      this.currentTabId = id;
      if (this.firstLoad) {
        return;
      }
      this.getReportingData(id);
    },
    async getReportingData(id, force = false, showLoadingModal = true) {
      try {
        if (showLoadingModal) {
          this.showModal('Please wait while the report is being generated.', 'Loading....');
        }
        switch (id) {
          case "tab-vbi":
            //fetch reporting for VBI
            if (this.updateRequireForVBITab == true || force) {
              const vbiData = await axios.post("/api/v1/administration/reporting/getReportDataForVBI", {filter: this.filters});
              this.vbiTabGraph = vbiData.data.graph;
              if (this.vbiTabGraph != null) {
                this.roundedNaf = this.vbiTabGraph.lenderBreakDownNAF.datasets[0].data.map(naf => {
                  return Math.round(naf);
                })
                this.roundedPodNaf = this.vbiTabGraph.lenderBreakDownNAF.datasets[0].data.map(pod_naf => {
                  return Math.round(pod_naf);
                })
                this.applicationIds = vbiData.data.appIds;
              }
              this.updateRequireForVBITab = false;
            }
            this.allowExport = true;
            this.allowSeeApplications = true;
            break;
          default :
            break;
        }
      } catch (err) {
        console.log(err)
      } finally {
        if (showLoadingModal) {
          this.closeModal();
        }
      }
    },
    showModal(message, title) {
      this.$modal.show(WaitingDialog, {
        title: title,
        message: message,
      });
    },
    closeModal() {
      if (this.$modal.submit) {
        this.$modal.submit();
      }
    },
    async getPreGraphData(force = false) {
      try {
        this.showModal('Please wait while the filtering is being generated.', 'Loading....');
        let response = await this.$store.dispatch("ensure_pre_graph_data", force);
        this.brokerList = response.brokerList;
        this.introducerList = response.introducerList;
        this.filters.dropdown.pod = this.$store.getters.user.user_pod;
        this.addExclFloorPlan();
        this.addExternalBroker();
      } catch (e) {
        console.error(e);
      } finally {
        this.closeModal();
      }
    },
    async getPodBasedBrokers(pod) {
      if (pod != 'External Brokers' && pod != 'All') {
        this.podBrokerList = this.brokerList[pod];
      } else {
        let newList = new Object();
        if (pod == 'All') {
          //if key is all show all the brokers
          Object.keys(this.brokerList).reduce((obj, key) => {
            Object.keys(this.brokerList[key]).forEach((email) => {
              newList[email] = this.brokerList[key][email];
            });
            return obj;
          }, {});
        } else {
          //if key is internal broker exclude it
          Object.keys(this.brokerList).reduce((obj, key) => {
            if (key != 'Internal Broker') {
              Object.keys(this.brokerList[key]).forEach((email) => {
                newList[email] = this.brokerList[key][email];
              });
            }
            return obj;
          }, {});
        }
        this.podBrokerList = newList;
      }
    },
    addExclFloorPlan() {
      //add "excl floorplan lenders" to the front of options.lenders
      this.options.lenders.unshift("Exclude Floorplan Lenders");
    },
    addExternalBroker(){
      //add external brokers pod to the front of options.brokers
      this.options.broker_pods.push("External Brokers");

      //add All Optiosn to roles
      this.options.roles.unshift("All");
      //add all to the front
      this.options.broker_pods.unshift("All");
    },
    resetFilters() {
      this.filters = newReportingFilters();
      this.setRequireToFetchNewDataForAllTab();
      this.getReportingData(this.currentTabId);
    },
    applyFilter() {
      this.firstLoad = false;
      this.setRequireToFetchNewDataForAllTab();
      this.getReportingData(this.currentTabId);
    },
    setRequireToFetchNewDataForAllTab() {
      this.updateRequireForVBITab = true;
    },
    exportApplications(){
      this.filters.force = true;
      this.filters.export = true;
      this.firstLoad = false;
      this.setRequireToFetchNewDataForAllTab();
      this.getReportingData(this.currentTabId, false, false);
      this.filters.export = false;
      this.filters.force = false;
      this.showNotification("Your export is being processed in the background. You will be notified via email once its complete.", 'Processing....');
    },
    showNotification(message, title) {
      this.$modal.show(AlertDialog, {
        title: title,
        message: message,
      });
    },
    async getApplicationList(appIds) {
      try {
        this.showModal('Please wait while the getting the application list.', 'Loading....');
        this.applicationIds = appIds;
        // reset pagination
        this.$store.commit('set_pagination', {
            backend_pagination: true,
            per_page: 50,
            page: 1
        });
        const pagination = this.$store.state.pagination;
        const response = await axios.post("/api/v1/reporting/getApplicationMetaData", {appIds: appIds, pagination: pagination});
        this.$store.commit('set_pagination', response.data.pagination);
        this.totalApplications = response.data.results;
        this.showApplications = true;
      } catch (e) {
        console.log(e);
      }
      this.closeModal();
    },
    async refreshData() {
      try {
        this.paginationLoading = true;
        const pagination = this.$store.state.pagination;
        const response = await axios.post("/api/v1/reporting/getApplicationMetaData", {appIds: this.applicationIds, pagination: pagination});
        this.$store.commit('set_pagination', response.data.pagination);
        this.totalApplications = response.data.results;
      } catch (e) {
        console.log(e);
      } finally {
        this.paginationLoading = false;
      }
    }
  },
  async mounted() {
    try {
      await this.getPreGraphData();
    } catch (err) {
      console.log(err);
    }

    if (this.user.email == "rodney@ausloans.com.au"
      || this.user.email == "jamie.gudsell@ausloans.com.au"
      || this.user.email == "benny.misikea@ausloans.com.au") {
      this.showVBI = true;
      this.getReportingData('tab-vbi');
      this.firstLoad = false;
    }
  },
  computed: {
    ...mapGetters([
      "options",
      "userHasPermission",
      "userHasRole",
      "user",
    ])
  },
  watch:{
    //watch changes in filter pod
    'filters.dropdown.pod'() {
      this.filters.dropdown.broker = [];
      this.getPodBasedBrokers(this.filters.dropdown.pod);
    },
    'filters.dropdown.asset_type'() {
      if (typeof(this.filters.dropdown.asset_type) === 'string' &&
        this.filters.dropdown.asset_type.length > 0 &&
        (this.filters.dropdown.asset_type != 'All')) {
        this.filters.dropdown.asset_type = [this.filters.dropdown.asset_type];
      }
    },
    'filters.dropdown.broker'() {
      if (typeof(this.filters.dropdown.broker) === 'string' &&
        this.filters.dropdown.broker.length > 0 &&
        this.filters.dropdown.broker != 'All') {
        this.filters.dropdown.broker = [this.filters.dropdown.broker];
      }
    },
    'filters.dropdown.lender'() {
      if (typeof(this.filters.dropdown.lender) === 'string' &&
        this.filters.dropdown.lender.length > 0 &&
        this.filters.dropdown.lender != 'All') {
        this.filters.dropdown.lender = [this.filters.dropdown.lender];
      }
    },
    'filters.date_filter.end_date'() {
      if (!this.filters.date_filter.end_date) {
        this.filters.date_filter.end_date = moment().format('YYYY-MM-DD');
      }
    },
    'filters.date_filter.start_date'() {
      if (!this.filters.date_filter.start_date) {
        this.filters.date_filter.start_date = moment().startOf('month').format('YYYY-MM-DD');
      }
    },
  },
  async beforeCreate() {
    const params = {
      types: [
        'broker_pods',
        'lenders',
        'loan_asset_type',
        'roles',
      ],
    }
    await this.$store.dispatch('fetch_types', { params });
  },
}
</script>

<style scoped lang="scss">
.coming-soon {
  text-align: center;
}

.filter-box {
  margin-top: 10px;
}

.application-popup-overlay {
  opacity: 0.7;
  background: #000;
  width: 100%;
  height: 100%;
  z-index: 5;
  top: 0;
  left: 0;
  position: fixed;
}

.application-popup {
  position: absolute;
  background: white;
  border: 1px grey solid;
  overflow-y:scroll;
  overflow-x:hidden;
  height: 75%;
  z-index: 6;
  top: 10%;
  left: 10%;
  right: 10%;
  padding-bottom: 20px;
}
</style>
